import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { ChargerAttributes } from '../models/charger-model';
import { getPartnerChargers } from '../services/partner-service';
import { getPartnerTermsStatus, getCurrentPartnerTerms } from '../services/partner-terms-service';
import { isSuccessful } from '../services/api-requests';
import { notificationService } from '../services/notification-service';
// @ts-ignore - Platform-specific file (.native/.web)
import PartnerTermsModal from '../components/partner/PartnerTermsModal';
// @ts-ignore - Platform-specific file (.native/.web)
import PartnerTermsReviewModal from '../components/partner/PartnerTermsReviewModal';
// @ts-ignore - Platform-specific file (.native/.web)
import PartnershipActivatedModal from '../components/partner/PartnershipActivatedModal';
import { useNavigation } from '@react-navigation/native';
import { HeaderWithMenu } from '../components/menu';

const SplitPartnerships: React.FC = () => {
    const navigation = useNavigation<any>();
    const [loading, setLoading] = useState(false);
    const [partnership, setPartnership] = useState<ChargerAttributes | null>(null);
    const [isPending, setIsPending] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [termsNotAccepted, setTermsNotAccepted] = useState(false);

    useEffect(() => {
        fetchPartnership();
    }, []);

    const fetchPartnership = async () => {
        try {
            setLoading(true);
            
            // First, fetch partner chargers to check if user has any split chargers
            const chargersResponse = await getPartnerChargers();
            
            console.log('Partner chargers response:', chargersResponse);
            
            // Check if response indicates that terms need to be accepted (error status with requiresAgreement)
            if (!isSuccessful(chargersResponse.status)) {
                const errorPayload = chargersResponse.payload as any;
                console.log('Error payload:', errorPayload);
                
                if (errorPayload?.requiresAgreement && errorPayload?.agreementType === 'SPLIT_PARTNER_TERMS') {
                    // Terms need to be accepted - show terms acceptance UI
                    console.log('Terms acceptance required - showing terms UI');
                    setTermsNotAccepted(true);
                    setIsPending(true);
                    setPartnership(null);
                    setLoading(false);
                    return;
                } else {
                    // Some other error occurred
                    console.error('Failed to load partner chargers:', errorPayload);
                    notificationService.sendNotification('error', errorPayload?.message || 'Failed to load partnership data');
                    setLoading(false);
                    return;
                }
            }
            
            // Success response - process chargers
            if (isSuccessful(chargersResponse.status) && chargersResponse.payload) {
                const chargers = (chargersResponse.payload as any)?.chargers || [];
                
                // Find the first charger with split permission
                const splitCharger = chargers.find((c: ChargerAttributes) => 
                    c.permissions?.includes('split')
                );

                // If no split chargers, don't show terms acceptance UI
                if (!splitCharger) {
                    setPartnership(null);
                    setTermsNotAccepted(false);
                    setIsPending(false);
                    setLoading(false);
                    return;
                }

                // User has split chargers - now check terms status
                const termsResponse = await getPartnerTermsStatus('SPLIT_PARTNER_TERMS');
                const termsAccepted = termsResponse.payload?.hasAgreement || false;

                // If terms not accepted, show terms acceptance UI
                if (!termsAccepted) {
                    setTermsNotAccepted(true);
                    setIsPending(true);
                    setLoading(false);
                    return;
                }

                // Terms accepted and has split charger
                setPartnership(splitCharger);
                setIsPending(false);
                setTermsNotAccepted(false);
            }
        } catch (error: any) {
            console.error('Exception fetching partnership:', error);
            
            // Check if the error response indicates that terms need to be accepted
            if (error?.payload?.requiresAgreement && error?.payload?.agreementType === 'SPLIT_PARTNER_TERMS') {
                // Terms need to be accepted - show terms acceptance UI
                setTermsNotAccepted(true);
                setIsPending(true);
                setPartnership(null);
                setLoading(false);
                return;
            }
            
            // Show error notification for unexpected errors
            notificationService.sendNotification('error', error?.message || 'Failed to load partnership');
        } finally {
            setLoading(false);
        }
    };

    const handleTermsSuccess = () => {
        setShowTermsModal(false);
        setShowSuccessModal(true);
        fetchPartnership(); // Refresh data after accepting terms
    };

    const handleViewPartnerships = () => {
        setShowSuccessModal(false);
        fetchPartnership();
    };

    const handleGoToDashboard = () => {
        setShowSuccessModal(false);
        navigation.navigate('PartnerEarningsDashboard');
    };

    if (loading) {
        return (
            <SafeAreaView style={styles.container} edges={['top']}>
                <HeaderWithMenu forceHamburger title="Split Partnership" insideSafeArea />
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#39BEB7" />
                    <Text style={styles.loadingText}>Loading partnership...</Text>
                </View>
            </SafeAreaView>
        );
    }

    // Show terms acceptance UI if terms haven't been accepted yet
    if (termsNotAccepted) {
        return (
            <SafeAreaView style={styles.container} edges={['top']}>
                <HeaderWithMenu forceHamburger title="Split Partnership" insideSafeArea />
                <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
                    <Text style={styles.mainTitle}>Split Partner Terms</Text>

                    {/* Terms Required Card */}
                    <View style={styles.partnershipCard}>
                        <View style={styles.termsIconContainer}>
                            <Ionicons name="document-text" size={48} color="#39BEB7" />
                        </View>

                        <Text style={styles.termsTitle}>Terms & Conditions Required</Text>
                        <Text style={styles.termsDescription}>
                            You've been invited to participate in MeanderEV's Revenue Share as a Designated Partner. Before you can view and earn from your split partnerships, you must review and accept the Split Partner Terms and Conditions.
                        </Text>

                        <Text style={styles.termsNote}>
                            This is a one-time agreement that applies to all Revenue-Share Chargers on your account.
                        </Text>

                        <Pressable
                            style={styles.reviewTermsButton}
                            onPress={() => setShowTermsModal(true)}
                        >
                            <Ionicons name="document-text-outline" size={20} color="#FFFFFF" />
                            <Text style={styles.reviewTermsButtonText}>Review & Accept Terms</Text>
                        </Pressable>
                    </View>
                </ScrollView>

                {/* Modals */}
                <PartnerTermsModal
                    visible={showTermsModal}
                    onClose={() => setShowTermsModal(false)}
                    onSuccess={handleTermsSuccess}
                />

                <PartnershipActivatedModal
                    visible={showSuccessModal}
                    onViewPartnerships={handleViewPartnerships}
                    onGoToDashboard={handleGoToDashboard}
                />
            </SafeAreaView>
        );
    }

    if (!partnership) {
        return (
            <SafeAreaView style={styles.container} edges={['top']}>
                <HeaderWithMenu forceHamburger title="Split Partnership" insideSafeArea />
                <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
                    <Text style={styles.mainTitle}>Split Partnership</Text>
                    <View style={styles.emptyState}>
                        <Ionicons name="briefcase-outline" size={64} color="#2A2D35" />
                        <Text style={styles.emptyTitle}>No Split Partnership</Text>
                        <Text style={styles.emptyText}>
                            You don't have a split partnership yet. When a host invites you to become a split partner, it will appear here.
                        </Text>
                    </View>
                </ScrollView>
            </SafeAreaView>
        );
    }

    const hostName = partnership.activeSplits?.[0]?.partnerUsername || 'Meander';
    const splitPercentage = partnership.activeSplits?.[0]?.splitPercentage || 0;

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <HeaderWithMenu forceHamburger title="Split Partnership" insideSafeArea />
            <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
                <Text style={styles.mainTitle}>Split Partnership</Text>

                {/* Partnership Card */}
                <View style={styles.partnershipCard}>
                    <View style={styles.detailsSection}>
                        <View style={styles.detailRow}>
                            <Ionicons name="person-outline" size={18} color="#8D8E90" />
                            <Text style={styles.detailLabel}>Host:</Text>
                            <Text style={styles.detailValue}>{hostName}</Text>
                        </View>

                        {splitPercentage > 0 && (
                            <View style={styles.detailRow}>
                                <Ionicons name="cash-outline" size={18} color="#51B868" />
                                <Text style={styles.detailLabel}>Your Split:</Text>
                                <Text style={[styles.detailValue, styles.splitValue]}>
                                    {splitPercentage}% of revenue
                                </Text>
                            </View>
                        )}

                        <View style={styles.detailRow}>
                            <Ionicons name="information-circle-outline" size={18} color="#8D8E90" />
                            <Text style={styles.detailLabel}>Status:</Text>
                            {isPending ? (
                                <View style={styles.statusBadge}>
                                    <Ionicons name="time-outline" size={16} color="#FFB800" />
                                    <Text style={styles.statusPending}>Awaiting Acceptance</Text>
                                </View>
                            ) : (
                                <View style={[styles.statusBadge, styles.statusBadgeActive]}>
                                    <Ionicons name="checkmark-circle" size={16} color="#51B868" />
                                    <Text style={styles.statusActive}>Active</Text>
                                </View>
                            )}
                        </View>
                    </View>

                    {isPending && (
                        <>
                            <View style={styles.divider} />
                            <View style={styles.actionSection}>
                                <Text style={styles.actionTitle}>Action Required</Text>
                                <Text style={styles.actionText}>
                                    To start earning from this partnership, you need to review and accept the Split Partner Terms and Conditions.
                                </Text>
                                <Pressable
                                    style={styles.acceptButton}
                                    onPress={() => setShowTermsModal(true)}
                                >
                                    <Ionicons name="document-text-outline" size={20} color="#FFFFFF" />
                                    <Text style={styles.acceptButtonText}>Review & Accept Terms</Text>
                                </Pressable>
                            </View>
                        </>
                    )}

                    {!isPending && (
                        <>
                            <View style={styles.divider} />
                            <View style={styles.activeSection}>
                                <Ionicons name="checkmark-circle" size={48} color="#51B868" />
                                <Text style={styles.activeTitle}>Partnership Active!</Text>
                                <Text style={styles.activeSubtext}>You're earning from this partnership</Text>
                                
                                <Pressable
                                    style={styles.reviewTermsLink}
                                    onPress={() => setShowReviewModal(true)}
                                >
                                    <Ionicons name="document-text-outline" size={16} color="#39BEB7" />
                                    <Text style={styles.reviewTermsLinkText}>Review Terms</Text>
                                </Pressable>
                            </View>
                        </>
                    )}
                </View>
            </ScrollView>

            {/* Modals */}
            <PartnerTermsModal
                visible={showTermsModal}
                onClose={() => setShowTermsModal(false)}
                onSuccess={handleTermsSuccess}
            />

            <PartnerTermsReviewModal
                visible={showReviewModal}
                onClose={() => setShowReviewModal(false)}
            />

            <PartnershipActivatedModal
                visible={showSuccessModal}
                onViewPartnerships={handleViewPartnerships}
                onGoToDashboard={handleGoToDashboard}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#1A1D23',
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: 20,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
    loadingText: {
        color: '#8D8E90',
        marginTop: 12,
        fontSize: 14,
    },
    mainTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: '#FFFFFF',
        marginBottom: 20,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        marginTop: 60,
    },
    emptyTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#FFFFFF',
        marginTop: 20,
        marginBottom: 12,
    },
    emptyText: {
        fontSize: 14,
        color: '#8D8E90',
        textAlign: 'center',
        lineHeight: 22,
    },
    partnershipCard: {
        backgroundColor: '#23262B',
        borderRadius: 16,
        padding: 20,
        borderWidth: 1,
        borderColor: '#2A2D35',
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
    },
    chargerName: {
        fontSize: 20,
        fontWeight: '700',
        color: '#FFFFFF',
        flex: 1,
    },
    chargerAddress: {
        fontSize: 14,
        color: '#8D8E90',
        marginBottom: 20,
        paddingLeft: 36,
    },
    detailsSection: {
        gap: 12,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    detailLabel: {
        fontSize: 14,
        color: '#8D8E90',
        fontWeight: '600',
        minWidth: 80,
    },
    detailValue: {
        fontSize: 14,
        color: '#FFFFFF',
        flex: 1,
    },
    splitValue: {
        color: '#51B868',
        fontWeight: '700',
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: 'rgba(255, 184, 0, 0.1)',
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 12,
    },
    statusBadgeActive: {
        backgroundColor: 'rgba(81, 184, 104, 0.1)',
    },
    statusPending: {
        fontSize: 13,
        color: '#FFB800',
        fontWeight: '600',
    },
    statusActive: {
        fontSize: 13,
        color: '#51B868',
        fontWeight: '600',
    },
    divider: {
        height: 1,
        backgroundColor: '#2A2D35',
        marginVertical: 20,
    },
    actionSection: {
        alignItems: 'center',
    },
    actionTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#FFB800',
        marginBottom: 8,
    },
    actionText: {
        fontSize: 14,
        color: '#8D8E90',
        textAlign: 'center',
        lineHeight: 20,
        marginBottom: 16,
    },
    acceptButton: {
        flexDirection: 'row',
        backgroundColor: '#39BEB7',
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    acceptButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
    },
    activeSection: {
        alignItems: 'center',
    },
    activeTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#51B868',
        marginTop: 12,
        marginBottom: 8,
    },
    activeSubtext: {
        fontSize: 14,
        color: '#8D8E90',
        textAlign: 'center',
        lineHeight: 20,
        marginBottom: 16,
    },
    reviewTermsLink: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#39BEB7',
        backgroundColor: 'rgba(57, 190, 183, 0.1)',
    },
    reviewTermsLinkText: {
        color: '#39BEB7',
        fontSize: 13,
        fontWeight: '600',
    },
    activeText: {
        fontSize: 14,
        color: '#8D8E90',
        textAlign: 'center',
        lineHeight: 20,
    },
    termsIconContainer: {
        alignItems: 'center',
        marginBottom: 20,
    },
    termsTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#FFFFFF',
        textAlign: 'center',
        marginBottom: 12,
    },
    termsDescription: {
        fontSize: 15,
        color: '#FFFFFF',
        textAlign: 'center',
        lineHeight: 22,
        marginBottom: 16,
    },
    termsNote: {
        fontSize: 13,
        color: '#8D8E90',
        textAlign: 'center',
        lineHeight: 20,
        marginBottom: 24,
        fontStyle: 'italic',
    },
    reviewTermsButton: {
        flexDirection: 'row',
        backgroundColor: '#39BEB7',
        paddingVertical: 14,
        paddingHorizontal: 24,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    reviewTermsButtonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
});

export default SplitPartnerships;

